import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { TicketEvent } from '../../types';

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<TicketEvent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      const { data } = await supabase
        .from('ticket_events')
        .select('*')
        .eq('id', id)
        .single();
      setEvent(data);
      setLoading(false);
    };
    fetchEvent();
  }, [id]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (cents: number, currency: string) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: currency 
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-[#8E8E93]">Evento no encontrado</p>
        <Link to="/tickets" className="text-primary mt-2 block">Volver a boletos</Link>
      </div>
    );
  }

  const available = event.capacity - event.sold_count;

  return (
    <div className="space-y-4">
      <Link to="/tickets" className="text-primary">← Volver</Link>
      
      {event.image_url && (
        <img src={event.image_url} alt={event.title} className="w-full h-48 object-cover rounded-lg" />
      )}

      <div className="card p-6">
        <h1 className="text-2xl font-bold text-white mb-4">{event.title}</h1>
        
        <div className="space-y-3 text-[#B3B3B3]">
          <p>📅 {formatDate(event.event_date)}</p>
          {event.location && <p>📍 {event.location}</p>}
        </div>

        {event.description && (
          <div className="mt-6">
            <h2 className="font-semibold text-[#E5E5EA] mb-2">Descripción</h2>
            <p className="text-[#B3B3B3]">{event.description}</p>
          </div>
        )}

        <div className="mt-6 p-4 bg-[#2C2C2E] rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[#B3B3B3]">Precio</span>
            <span className="text-2xl font-bold text-primary">
              {formatPrice(event.price_cents, event.currency)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[#B3B3B3]">Disponibles</span>
            <span className={`font-medium ${available > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {available > 0 ? `${available} boletos` : 'Agotado'}
            </span>
          </div>
        </div>

        {available > 0 && event.stripe_session_id ? (
          <button className="btn-primary w-full mt-6 py-3">
            Comprar Boleto
          </button>
        ) : available > 0 ? (
          <button className="btn-primary w-full mt-6 py-3">
            Reservar Boleto
          </button>
        ) : (
          <button className="btn-outline w-full mt-6 py-3" disabled>
            No disponible
          </button>
        )}
      </div>
    </div>
  );
}
